/**
 * IT Module Routes
 * Users, Forms, Logs, Services, QR, Phone Topups, Zimmet, Meetings
 *
 * ⚠️ CRITICAL: Contains /it/users/grant_permission route for authorization system
 */
import React, { lazy, Suspense } from 'react';
import { Spin, Result } from 'antd';
import { Navigate } from 'react-router-dom';
import { RouteItem } from './index';
import { PageGuard } from '../components/common/security/ProtectedRoute';
import {
  HomeOutlined, FileOutlined, PlusOutlined, FileTextOutlined,
  UserOutlined, CalendarOutlined, FormOutlined, OrderedListOutlined, ExperimentOutlined,
  DollarOutlined, WalletOutlined, DatabaseOutlined, TagsOutlined, ShopOutlined,
  ClusterOutlined, BranchesOutlined, QrcodeOutlined, CloudServerOutlined
} from '@ant-design/icons';

// Lazy load all IT components
// Users (⚠️ CRITICAL FOR AUTHORIZATION)
const ItUserPermissionsCreate = lazy(() => import('../pages/it/users/ItUserPermissionsCreate'));
const ItUserCreate = lazy(() => import('../pages/it/users/ItUserCreate'));
const ItUserList = lazy(() => import('../pages/it/users/ItUserList'));
const ItUserEdit = lazy(() => import('../pages/it/users/ItUserEdit'));

// Forms
const ItFormList = lazy(() => import('../pages/it/forms/ItFormList'));
const ItFormsCreate = lazy(() => import('../pages/it/forms/ItFormsCreate'));
const ItFormFill = lazy(() => import('../pages/it/forms/ItFormFill'));
const ItFormEdit = lazy(() => import('../pages/it/forms/ItFormEdit'));
const ItFormView = lazy(() => import('../pages/it/forms/ItFormView'));
const ItFormGroupCreate = lazy(() => import('../pages/it/forms/ItFormGroupCreate'));
const FormDefinitionsList = lazy(() => import('../components/common/Forms/FormDefinitionsList'));
const FormDefinitionEditor = lazy(() => import('../components/common/Forms/FormDefinitionEditor'));
const FormRecordDetailsList = lazy(() => import('../components/common/Forms/FormRecordDetailsList'));
const PdfToReactFormGenerator = lazy(() => import('../components/common/Forms/PdfToReactFormGenerator'));

// Logs
const ItLogsListTkfAdmin = lazy(() => import('../pages/it/logs/ItLogsListTkfAdmin'));
const ItUserLoginLogList = lazy(() => import('../pages/it/users/ItUserLoginLogList'));

// Tools
const ItQrTools = lazy(() => import('../pages/it/QrTools'));

// Phone Topups
const ItPhoneTopupsMain = lazy(() => import('../pages/it/ItPhoneTopupsMain'));

// Meetings
const ItMeetingsMain = lazy(() => import('../pages/it/meetings/ItMeetingsMain'));

// Zimmet - Dashboard
const ItZimmetDashboard = lazy(() => import('../pages/it/zimmet/ItZimmetDashboard'));

// Zimmet - Operations
const ItZimmetList = lazy(() => import('../pages/it/zimmet/ItZimmetList'));
const ItZimmetCreate = lazy(() => import('../pages/it/zimmet/ItZimmetCreate'));
const ItZimmetDetail = lazy(() => import('../pages/it/zimmet/ItZimmetDetail'));
const ItZimmetEdit = lazy(() => import('../pages/it/zimmet/ItZimmetEdit'));
const ItZimmetUserPrint = lazy(() => import('../pages/it/zimmet/ItZimmetUserPrint'));
const ItZimmetUserPrintSelect = lazy(() => import('../pages/it/zimmet/ItZimmetUserPrintSelect'));
const ItZimmetLogList = lazy(() => import('../pages/it/zimmet/ItZimmetLogList'));
const ItZimmetScan = lazy(() => import('../pages/it/zimmet/ItZimmetScan'));

// Zimmet - Envanter
const ItEnvanterList = lazy(() => import('../pages/it/envanter/ItEnvanterList'));
const ItEnvanterCreate = lazy(() => import('../pages/it/envanter/ItEnvanterCreate'));
const ItEnvanterDetail = lazy(() => import('../pages/it/envanter/ItEnvanterDetail'));
const ItEnvanterEdit = lazy(() => import('../pages/it/envanter/ItEnvanterEdit'));
const ItEnvanterImport = lazy(() => import('../pages/it/envanter/ItEnvanterImport'));
const ItEnvanterYonetimi = lazy(() => import('../pages/it/envanter/ItEnvanterYonetimi'));
const BilgisayarAdlariListe = lazy(() => import('../pages/it/envanter/BilgisayarAdlariListe'));

// Zimmet - Tanımlar
const ItKategorilerList = lazy(() => import('../pages/it/zimmet/ItKategorilerList'));
const ItKategorilerCreate = lazy(() => import('../pages/it/zimmet/ItKategorilerCreate'));
const ItKategorilerEdit = lazy(() => import('../pages/it/zimmet/ItKategorilerEdit'));
const ItMarkalarList = lazy(() => import('../pages/it/zimmet/ItMarkalarList'));
const ItMarkalarCreate = lazy(() => import('../pages/it/zimmet/ItMarkalarCreate'));
const ItMarkalarEdit = lazy(() => import('../pages/it/zimmet/ItMarkalarEdit'));
const ItTedarikcilerList = lazy(() => import('../pages/it/zimmet/ItTedarikcilerList'));
const ItTedarikcilerCreate = lazy(() => import('../pages/it/zimmet/ItTedarikcilerCreate'));
const ItTedarikcilerEdit = lazy(() => import('../pages/it/zimmet/ItTedarikcilerEdit'));
const ItModellerList = lazy(() => import('../pages/it/zimmet/ItModellerList'));
const ItModellerCreate = lazy(() => import('../pages/it/zimmet/ItModellerCreate'));
const ItModellerEdit = lazy(() => import('../pages/it/zimmet/ItModellerEdit'));
const ItVaryantlarList = lazy(() => import('../pages/it/zimmet/ItVaryantlarList'));
const ItVaryantlarCreate = lazy(() => import('../pages/it/zimmet/ItVaryantlarCreate'));
const ItVaryantlarDetail = lazy(() => import('../pages/it/zimmet/ItVaryantlarDetail'));
const ItVaryantlarEdit = lazy(() => import('../pages/it/zimmet/ItVaryantlarEdit'));
const ItZimmetTanimlar = lazy(() => import('../pages/it/zimmet/ItZimmetTanimlar'));

// UnderConstruction component
const UnderConstruction = ({ title }: { title: string }) => (
  <Result status="info" title={`${title} Module`} subTitle="Under Construction." />
);

// Suspense wrapper
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }} />}>
    <Component />
  </Suspense>
);

const guard = (node: React.ReactNode) => (<PageGuard>{node}</PageGuard>);

export const itRoutes: Record<string, RouteItem> = {
  it: {
    path: '/it',
    element: <Navigate to="/it/dashboard" replace />,
    label: 'IT',
    showInMenu: false,
    children: {
      dashboard: {
        path: '/it/dashboard',
        element: guard(<UnderConstruction title="IT" />),
        label: 'Dashboard',
        icon: <HomeOutlined />,
        showInMenu: true
      },
      forms: {
        path: '/it/forms',
        element: guard(withSuspense(ItFormList)),
        label: 'Forms',
        icon: <FormOutlined />,
        showInMenu: true,
        grandchildren: {
          list: {
            path: '/it/forms/list',
            element: guard(withSuspense(ItFormList)),
            label: 'Forms',
            icon: <OrderedListOutlined />,
            showInMenu: true
          },
          definitions: {
            path: '/it/forms/definitions',
            element: guard(withSuspense(FormDefinitionsList)),
            label: 'Form Definitions',
            icon: <OrderedListOutlined />,
            showInMenu: true
          },
          definitions_create: {
            path: '/it/forms/definitions/create',
            element: guard(withSuspense(PdfToReactFormGenerator)),
            label: 'Create Form Definition',
            showInMenu: false
          },
          definitions_edit: {
            path: '/it/forms/definitions/edit/:id',
            element: guard(withSuspense(FormDefinitionEditor)),
            label: 'Edit Form Definition',
            showInMenu: false
          },
          list_record_details: {
            path: '/it/forms/list_record_details',
            element: guard(withSuspense(FormRecordDetailsList)),
            label: 'Form Record Details',
            icon: <OrderedListOutlined />,
            showInMenu: true
          },
          group: {
            path: '/it/forms/gr_create',
            element: guard(withSuspense(ItFormGroupCreate)),
            label: 'New Form Group',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          create: {
            path: '/it/forms/create',
            element: guard(withSuspense(ItFormsCreate)),
            label: 'Create Form',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          fill: {
            path: '/it/forms/fill',
            element: guard(withSuspense(ItFormFill)),
            label: 'Fill Form',
            showInMenu: true
          },
          edit: {
            path: '/it/forms/edit/:id',
            element: guard(withSuspense(ItFormEdit)),
            label: 'Edit Form',
            showInMenu: false
          },
          view: {
            path: '/it/forms/view/:id',
            element: guard(withSuspense(ItFormView)),
            label: 'View Form',
            showInMenu: false
          }
        }
      },
      aiSystems: {
        path: '/it/ai-systems',
        element: guard(<UnderConstruction title="AI Systems" />),
        label: 'AI Systems',
        icon: <ExperimentOutlined />,
        showInMenu: true
      },
      users: {
        path: '/it/users',
        element: guard(<UnderConstruction title="Users" />),
        label: 'Users',
        icon: <UserOutlined />,
        showInMenu: true,
        children: {
          // ⚠️ CRITICAL: Authorization route - DO NOT MODIFY PATH
          grant: {
            path: '/it/users/grant_permission',
            element: guard(withSuspense(ItUserPermissionsCreate)),
            label: 'Permissions',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          create: {
            path: '/it/users/create',
            element: guard(withSuspense(ItUserCreate)),
            label: 'Create User',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          list: {
            path: '/it/users/list',
            element: guard(withSuspense(ItUserList)),
            label: 'User List',
            icon: <OrderedListOutlined />,
            showInMenu: true
          },
          edit: {
            path: '/it/users/edit/:id',
            element: guard(withSuspense(ItUserEdit)),
            label: 'Edit User',
            showInMenu: false
          }
        }
      },
      meetings: {
        path: '/it/meetings',
        element: guard(withSuspense(ItMeetingsMain)),
        label: 'Meetings',
        icon: <CalendarOutlined />,
        showInMenu: true
      },
      logs: {
        path: '/it/logs',
        element: guard(withSuspense(ItLogsListTkfAdmin)),
        label: 'TKF Admin Logları',
        icon: <FileTextOutlined />,
        showInMenu: true,
        children: {
          list: {
            path: '/it/logs/list',
            element: guard(withSuspense(ItLogsListTkfAdmin)),
            label: 'TKF Admin Logları',
            icon: <FileTextOutlined />,
            showInMenu: true
          },
          userLoginLogs: {
            path: '/it/logs/user-login-logs',
            element: guard(withSuspense(ItUserLoginLogList)),
            label: 'User Login Logs',
            icon: <FileTextOutlined />,
            showInMenu: true
          }
        }
      },
      userLoginLogsRedirect: {
        path: '/it/user-login-logs',
        element: <Navigate to="/it/logs/user-login-logs" replace />,
        showInMenu: false
      },
      
      qr: {
        path: '/it/qr',
        element: guard(withSuspense(ItQrTools)),
        label: 'QR',
        icon: <QrcodeOutlined />,
        showInMenu: true
      },
      phoneTopups: {
        path: '/it/phone-topups',
        element: guard(withSuspense(ItPhoneTopupsMain)),
        label: 'Telefon Yüklemeleri',
        icon: <DollarOutlined />,
        showInMenu: true
      },
      zimmet: {
        path: '/it/zimmet',
        element: <Navigate to="/it/zimmet/dashboard" replace />,
        label: 'Zimmet',
        icon: <WalletOutlined />,
        showInMenu: true,
        children: {
          dashboard: {
            path: '/it/zimmet/dashboard',
            element: guard(withSuspense(ItZimmetDashboard)),
            label: 'Dashboard',
            icon: <HomeOutlined />,
            showInMenu: true
          },
          zimmet_islemleri: {
            path: '/it/zimmet/zimmet-islemleri',
            element: <Navigate to="/it/zimmet/list" replace />,
            label: 'Zimmet İşlemleri',
            icon: <FileOutlined />,
            showInMenu: true,
            grandchildren: {
              scan: {
                path: '/it/zimmet/scan',
                element: guard(withSuspense(ItZimmetScan)),
                label: 'Scan',
                icon: <FileOutlined />,
                showInMenu: true
              },
              list: {
                path: '/it/zimmet/list',
                element: guard(withSuspense(ItZimmetList)),
                label: 'Zimmet Listesi',
                icon: <OrderedListOutlined />,
                showInMenu: true
              },
              user_print: {
                path: '/it/zimmet/user-print/:personnelId',
                element: guard(withSuspense(ItZimmetUserPrint)),
                label: 'Zimmet Formu',
                icon: <FileTextOutlined />,
                showInMenu: false
              },
              user_print_select: {
                path: '/it/zimmet/user-print',
                element: guard(withSuspense(ItZimmetUserPrintSelect)),
                label: 'Zimmet Formu',
                icon: <FileTextOutlined />,
                showInMenu: true
              },
              create: {
                path: '/it/zimmet/create',
                element: guard(withSuspense(ItZimmetCreate)),
                label: 'Yeni Zimmet Ekle',
                icon: <PlusOutlined />,
                showInMenu: false
              },
              detail: {
                path: '/it/zimmet/detail/:id',
                element: guard(withSuspense(ItZimmetDetail)),
                label: 'Zimmet Detay',
                showInMenu: false
              },
              edit: {
                path: '/it/zimmet/edit/:id',
                element: guard(withSuspense(ItZimmetEdit)),
                label: 'Zimmet Düzenle',
                showInMenu: false
              },
              logs: {
                path: '/it/zimmet/logs',
                element: guard(withSuspense(ItZimmetLogList)),
                label: 'Zimmet Logları',
                icon: <FileTextOutlined />,
                showInMenu: true
              }
            }
          },
          envanter_yonetimi: {
            path: '/it/zimmet/envanter',
            element: guard(withSuspense(ItEnvanterYonetimi)),
            label: 'Envanter Yönetimi',
            icon: <DatabaseOutlined />,
            showInMenu: true,
            grandchildren: {
              // Üst öğe menüde yalnızca açılır liste olduğundan Yönetim sayfasına buradan girilir
              envanter_dashboard: {
                path: '/it/zimmet/envanter/dashboard',
                element: guard(withSuspense(ItEnvanterYonetimi)),
                label: 'Dashboard',
                icon: <HomeOutlined />,
                showInMenu: true
              },
              envanter_list: {
                path: '/it/zimmet/envanter/list',
                element: guard(withSuspense(ItEnvanterList)),
                label: 'Envanter Listesi',
                icon: <FileOutlined />,
                showInMenu: true
              },
              envanter_create: {
                path: '/it/zimmet/envanter/create',
                element: guard(withSuspense(ItEnvanterCreate)),
                label: 'Yeni Envanter',
                icon: <PlusOutlined />,
                showInMenu: true
              },
              envanter_import: {
                path: '/it/zimmet/envanter/import',
                element: guard(withSuspense(ItEnvanterImport)),
                label: "TXT'den İçe Aktar",
                icon: <FileTextOutlined />,
                showInMenu: true
              },
              envanter_bilgisayar_adlari: {
                path: '/it/zimmet/envanter/bilgisayar-adlari',
                element: guard(withSuspense(BilgisayarAdlariListe)),
                label: 'Bilgisayar Adları',
                icon: <CloudServerOutlined />,
                showInMenu: true
              },
              envanter_detail: {
                path: '/it/zimmet/envanter/detail/:id',
                element: guard(withSuspense(ItEnvanterDetail)),
                label: 'Envanter Detay',
                showInMenu: false
              },
              envanter_edit: {
                path: '/it/zimmet/envanter/edit/:id',
                element: guard(withSuspense(ItEnvanterEdit)),
                label: 'Envanter Düzenle',
                showInMenu: false
              }
            }
          },
          tanimlar: {
            path: '/it/zimmet/tanimlar',
            element: guard(withSuspense(ItZimmetTanimlar)),
            label: 'Tanımlar',
            showInMenu: true,
            grandchildren: {
              kategoriler: {
                path: '/it/zimmet/kategoriler',
                element: <Navigate to="/it/zimmet/kategoriler/list" replace />,
                label: 'Kategoriler',
                icon: <TagsOutlined />,
                showInMenu: true,
                children: {
                  list: {
                    path: '/it/zimmet/kategoriler/list',
                    element: guard(withSuspense(ItKategorilerList)),
                    label: 'Kategori Listesi',
                    icon: <OrderedListOutlined />,
                    showInMenu: true
                  },
                  create: {
                    path: '/it/zimmet/kategoriler/create',
                    element: guard(withSuspense(ItKategorilerCreate)),
                    label: 'Yeni Kategori',
                    icon: <PlusOutlined />,
                    showInMenu: true
                  },
                  edit: {
                    path: '/it/zimmet/kategoriler/edit/:id',
                    element: guard(withSuspense(ItKategorilerEdit)),
                    label: 'Kategori Düzenle',
                    showInMenu: false
                  }
                }
              },
              markalar: {
                path: '/it/zimmet/markalar',
                element: <Navigate to="/it/zimmet/markalar/list" replace />,
                label: 'Markalar',
                icon: <TagsOutlined />,
                showInMenu: true,
                children: {
                  list: {
                    path: '/it/zimmet/markalar/list',
                    element: guard(withSuspense(ItMarkalarList)),
                    label: 'Marka Listesi',
                    icon: <OrderedListOutlined />,
                    showInMenu: true
                  },
                  create: {
                    path: '/it/zimmet/markalar/create',
                    element: guard(withSuspense(ItMarkalarCreate)),
                    label: 'Yeni Marka',
                    icon: <PlusOutlined />,
                    showInMenu: true
                  },
                  edit: {
                    path: '/it/zimmet/markalar/edit/:id',
                    element: guard(withSuspense(ItMarkalarEdit)),
                    label: 'Marka Düzenle',
                    showInMenu: false
                  }
                }
              },
              tedarikciler: {
                path: '/it/zimmet/tedarikciler',
                element: <Navigate to="/it/zimmet/tedarikciler/list" replace />,
                label: 'Tedarikçiler',
                icon: <ShopOutlined />,
                showInMenu: true,
                children: {
                  list: {
                    path: '/it/zimmet/tedarikciler/list',
                    element: guard(withSuspense(ItTedarikcilerList)),
                    label: 'Tedarikçi Listesi',
                    icon: <OrderedListOutlined />,
                    showInMenu: true
                  },
                  create: {
                    path: '/it/zimmet/tedarikciler/create',
                    element: guard(withSuspense(ItTedarikcilerCreate)),
                    label: 'Yeni Tedarikçi',
                    icon: <PlusOutlined />,
                    showInMenu: true
                  },
                  edit: {
                    path: '/it/zimmet/tedarikciler/edit/:id',
                    element: guard(withSuspense(ItTedarikcilerEdit)),
                    label: 'Tedarikçi Düzenle',
                    showInMenu: false
                  }
                }
              },
              modeller: {
                path: '/it/zimmet/modeller',
                element: <Navigate to="/it/zimmet/modeller/list" replace />,
                label: 'Modeller',
                icon: <ClusterOutlined />,
                showInMenu: true,
                children: {
                  list: {
                    path: '/it/zimmet/modeller/list',
                    element: guard(withSuspense(ItModellerList)),
                    label: 'Model Listesi',
                    icon: <OrderedListOutlined />,
                    showInMenu: true
                  },
                  create: {
                    path: '/it/zimmet/modeller/create',
                    element: guard(withSuspense(ItModellerCreate)),
                    label: 'Yeni Model',
                    icon: <PlusOutlined />,
                    showInMenu: true
                  },
                  edit: {
                    path: '/it/zimmet/modeller/edit/:id',
                    element: guard(withSuspense(ItModellerEdit)),
                    label: 'Model Düzenle',
                    showInMenu: false
                  },
                  modellers_create_redirect: {
                    path: '/it/zimmet/modellers/create',
                    element: <Navigate to="/it/zimmet/modeller/create" replace />,
                    showInMenu: false
                  }
                }
              },
              varyantlar: {
                path: '/it/zimmet/varyantlar',
                element: <Navigate to="/it/zimmet/varyantlar/list" replace />,
                label: 'Varyantlar',
                icon: <BranchesOutlined />,
                showInMenu: true,
                children: {
                  list: {
                    path: '/it/zimmet/varyantlar/list',
                    element: guard(withSuspense(ItVaryantlarList)),
                    label: 'Varyant Listesi',
                    icon: <OrderedListOutlined />,
                    showInMenu: true
                  },
                  create: {
                    path: '/it/zimmet/varyantlar/create',
                    element: guard(withSuspense(ItVaryantlarCreate)),
                    label: 'Yeni Varyant',
                    icon: <PlusOutlined />,
                    showInMenu: true
                  },
                  edit: {
                    path: '/it/zimmet/varyantlar/edit/:id',
                    element: guard(withSuspense(ItVaryantlarEdit)),
                    label: 'Varyant Düzenle',
                    showInMenu: false
                  },
                  detail: {
                    path: '/it/zimmet/varyantlar/detail/:id',
                    element: guard(withSuspense(ItVaryantlarDetail)),
                    label: 'Varyant Detay',
                    showInMenu: false
                  }
                }
              }
            }
          }
        }
      }
    }
  }
};
